import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { UserPreferences } from '../types';

interface CheckoutNotification {
  type: 'success' | 'cancelled';
  message: string;
}

interface UseCheckoutNotificationParams {
  setUserPreferences: React.Dispatch<React.SetStateAction<UserPreferences | undefined>>;
}

interface UseCheckoutNotificationResult {
  checkoutNotification: CheckoutNotification | null;
  setCheckoutNotification: React.Dispatch<React.SetStateAction<CheckoutNotification | null>>;
}

export default function useCheckoutNotification({
  setUserPreferences,
}: UseCheckoutNotificationParams): UseCheckoutNotificationResult {
  const [checkoutNotification, setCheckoutNotification] = useState<CheckoutNotification | null>(null);

  // Handle checkout success/cancelled URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('checkout');
    if (checkoutResult === 'success') {
      setCheckoutNotification({
        type: 'success',
        message: 'Payment successful! Your subscription is now active.',
      });
      // Refresh preferences to pick up updated subscription status
      api.getPreferences().then((prefs) => {
        setUserPreferences(prefs);
      }).catch((err) => {
        console.error('Failed to refresh preferences after checkout:', err);
      });
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => setCheckoutNotification(null), 8000);
      return () => clearTimeout(timer);
    } else if (checkoutResult === 'cancelled') {
      setCheckoutNotification({
        type: 'cancelled',
        message: 'Checkout was cancelled.',
      });
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => setCheckoutNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [setUserPreferences]);

  return { checkoutNotification, setCheckoutNotification };
}
